import { useEffect } from "react";

/**
 * 背景 K 线：OKX 公共 WSS `tickers` / `BTC-USDT`（顶栏 BTC/ETH 走 `GetBinanceSpotEthBtcQuote`）。
 * `last`、`open24h` → 24h 涨跌幅 %。
 */

export interface Ripple {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
  color: string;
}

const MAX_POINTS = 200;
/** 过大会让折线「反应迟钝」；过小会刷屏 */
const MIN_TICKER_INTERVAL_MS = 100;

let globalPrices: number[] = [];
let globalTimestamps: number[] = [];
let globalBasePrice = 0;
let globalRipples: Ripple[] = [];
let globalOffset = 0;
let globalTicker24hPct = 0;
let initialized = false;
let tickerWs: WebSocket | null = null;
let listenerCount = 0;

function addRipple(newPrice: number, lastPrice: number) {
  const changePct = Math.abs((newPrice - lastPrice) / lastPrice);
  const dynamicMax = Math.min(60, Math.max(20, 20 + changePct * 50000));
  const dynamicOpacity = Math.min(0.8, Math.max(0.4, 0.4 + changePct * 2000));
  const isUp = newPrice >= lastPrice;
  globalRipples.push({
    x: 0,
    y: 0,
    radius: 3,
    maxRadius: dynamicMax,
    opacity: dynamicOpacity,
    color: isUp ? "34,197,94" : "239,68,68",
  });
  globalOffset = 1;
}

const OKX_PUBLIC_WS = "wss://ws.okx.com:8443/ws/v5/public";

function connectTickerWs() {
  if (
    tickerWs != null &&
    (tickerWs.readyState === WebSocket.OPEN ||
      tickerWs.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }
  if (tickerWs) {
    try {
      tickerWs.close();
    } catch {
      /* ignore */
    }
    tickerWs = null;
  }

  try {
    const socket = new WebSocket(OKX_PUBLIC_WS);
    tickerWs = socket;
    let lastTickAt = 0;

    socket.onopen = () => {
      try {
        socket.send(
          JSON.stringify({
            op: "subscribe",
            args: [{ channel: "tickers", instId: "BTC-USDT" }],
          })
        );
      } catch {
        /* ignore */
      }
    };

    socket.onmessage = event => {
      try {
        const raw = event.data;
        if (typeof raw !== "string") return;

        const msg = JSON.parse(raw) as {
          event?: string;
          arg?: { channel?: string; instId?: string };
          data?: Array<{
            last?: string;
            open24h?: string;
            ts?: string;
          }>;
        };

        if (msg.event === "ping") {
          try {
            socket.send(JSON.stringify({ event: "pong" }));
          } catch {
            /* ignore */
          }
          return;
        }
        if (msg.event === "subscribe" || msg.event === "error") {
          return;
        }

        if (
          msg.arg?.channel !== "tickers" ||
          !Array.isArray(msg.data) ||
          msg.data.length === 0
        ) {
          return;
        }

        const row = msg.data[0];
        const newPrice = parseFloat(row.last ?? "");
        if (isNaN(newPrice)) return;

        const open24h = parseFloat(row.open24h ?? "");
        if (!isNaN(open24h) && open24h !== 0) {
          globalTicker24hPct = ((newPrice - open24h) / open24h) * 100;
        }

        const now = Date.now();
        if (now - lastTickAt < MIN_TICKER_INTERVAL_MS) return;
        lastTickAt = now;

        const ts = parseInt(row.ts ?? "", 10);
        const tsMs = !isNaN(ts) ? ts : now;

        if (globalPrices.length === 0) {
          globalPrices.push(newPrice, newPrice);
          globalTimestamps.push(tsMs, tsMs);
          globalBasePrice = newPrice;
          return;
        }

        const lastPrice = globalPrices[globalPrices.length - 1];
        globalPrices.push(newPrice);
        globalTimestamps.push(tsMs);
        if (globalPrices.length > MAX_POINTS + 20) {
          globalPrices.shift();
          globalTimestamps.shift();
        }

        const recent = globalPrices.slice(-50);
        const s = [...recent].sort((a, b) => a - b);
        globalBasePrice = s[Math.floor(s.length / 2)];

        if (newPrice !== lastPrice) {
          addRipple(newPrice, lastPrice);
        }
      } catch {
        /* ignore */
      }
    };

    socket.onclose = () => {
      tickerWs = null;
      if (listenerCount > 0) setTimeout(() => connectTickerWs(), 2000);
    };
    socket.onerror = () => {
      try {
        socket.close();
      } catch {
        /* ignore */
      }
    };
  } catch {
    if (listenerCount > 0) setTimeout(() => connectTickerWs(), 2000);
  }
}

function initData() {
  if (!initialized) {
    initialized = true;
    // 原地清空，保持数组引用稳定，避免消费者闭包里的 ref 失效
    globalPrices.length = 0;
    globalTimestamps.length = 0;
    globalRipples.length = 0;
    globalBasePrice = 0;
    globalTicker24hPct = 0;
  }
  connectTickerWs();
}

function stopAll() {
  if (tickerWs) {
    try {
      tickerWs.close();
    } catch {
      /* ignore */
    }
    tickerWs = null;
  }
  initialized = false;
}

export function useKLineData() {
  useEffect(() => {
    listenerCount++;
    initData();
    return () => {
      listenerCount--;
      if (listenerCount <= 0) {
        listenerCount = 0;
        setTimeout(() => {
          if (listenerCount <= 0) stopAll();
        }, 3000);
      }
    };
  }, []);

  // 全部用 getter，确保渲染期被捕获后仍能读到最新的全局引用
  return {
    pricesRef: {
      get current() {
        return globalPrices;
      },
    },
    timestampsRef: {
      get current() {
        return globalTimestamps;
      },
    },
    basePriceRef: {
      get current() {
        return globalBasePrice;
      },
    },
    ripplesRef: {
      get current() {
        return globalRipples;
      },
    },
    ticker24hPctRef: {
      get current() {
        return globalTicker24hPct;
      },
    },
    offsetRef: {
      get current() {
        return globalOffset;
      },
      set current(v: number) {
        globalOffset = v;
      },
    },
    maxPoints: MAX_POINTS,
  };
}
